/**
 * PhaseTrailBlock — renders the phase-trail bubble above the last
 * assistant message (live and persisted).
 *
 * Accepts a structured `PhaseEvent[]` (labels pre-built server-side) so the
 * live trail growing via WS and the persisted trail loaded from message
 * metadata render identically — no client-side label logic, no drift.
 *
 * Replaces the legacy string-derived `statusHistory` in MessageList, which
 * collapsed individual peer transitions behind aggregate views. Every
 * PhaseEvent is its own line in the expanded list, so a 3-peer consult
 * surfaces all three terminal transitions (✓ or ✗) regardless of ordering.
 *
 * Live-mode affordances:
 *   - Spinning hourglass on events that are still active
 *   - Live-ticking `(Ns)` suffix on each active sub-item
 *   - Total turn-elapsed counter in the header button
 *   - Inactive events de-emphasized so the eye lands on what's live
 */
import React, { useEffect, useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import type { PhaseEvent } from '../../hooks/usePersonaToolProgress';
import { TrailmasterCrown } from '../personas/TrailmasterCrown';
import { usePersonaSessionRoles } from '../../hooks/usePersonaSessionRoles';

interface PhaseTrailBlockProps {
  /** True while a turn is actively in progress — shows the bouncing dots,
   *  the `loadingStatus` single-line label, and ticks the per-item
   *  elapsed counters. False after the turn completes (persisted trail). */
  isLoading: boolean;
  /** The live single-line status string (live mode only). */
  loadingStatus?: string;
  events: PhaseEvent[];
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  darkMode: boolean;
}

// Terminal-event kinds per entity type. An entity (peer / tool / main-agent
// phase) is considered "done" once we've seen any of these events for it.
const PEER_TERMINAL_KINDS = new Set([
  'chat_peer_call_completed',
  'chat_peer_call_failed',
]);
const TOOL_TERMINAL_KINDS = new Set([
  'chat_tool_completed',
  'chat_tool_timeout',
  'chat_tool_error',
  'chat_budget_exceeded',
]);

/**
 * Returns true if the event at `idx` represents an in-progress state that
 * hasn't yet seen its terminal counterpart anywhere later in the list.
 *
 * This is how the renderer differentiates "still thinking" from "already
 * finished" — a started event followed by a completion becomes inactive,
 * but an orphaned started event (tail of the live list) stays active and
 * gets the spinning hourglass + live counter.
 */
function isEventStillActive(
  event: PhaseEvent,
  idx: number,
  events: PhaseEvent[],
): boolean {
  const { kind, details } = event;
  const d = (details || {}) as Record<string, any>;

  if (kind === 'chat_peer_call_started') {
    const pid = d.peer_persona_id;
    for (let j = idx + 1; j < events.length; j++) {
      const later = events[j];
      if (
        PEER_TERMINAL_KINDS.has(later.kind) &&
        (later.details as any)?.peer_persona_id === pid
      ) {
        return false;
      }
    }
    return true;
  }

  if (kind === 'chat_tool_invoked') {
    const tcid = d.tool_call_id;
    for (let j = idx + 1; j < events.length; j++) {
      const later = events[j];
      if (
        TOOL_TERMINAL_KINDS.has(later.kind) &&
        (later.details as any)?.tool_call_id === tcid
      ) {
        return false;
      }
    }
    return true;
  }

  if (kind === 'chat_turn_heartbeat') {
    // Main-agent thinking is active until the next tool invocation, a
    // turn-completed event, or a heartbeat with a different phase.
    const phase = d.phase;
    for (let j = idx + 1; j < events.length; j++) {
      const later = events[j];
      if (
        later.kind === 'chat_tool_invoked' ||
        later.kind === 'chat_turn_completed' ||
        (later.kind === 'chat_turn_heartbeat' &&
          (later.details as any)?.phase !== phase)
      ) {
        return false;
      }
    }
    return true;
  }

  // Instantaneous events (resolved / completed / failed) aren't "active".
  return false;
}

/**
 * Split a label into its leading status emoji (if any) and the remainder.
 * Enables spinning just the ⏳ glyph on active rows without rotating the
 * text alongside it. Non-hourglass emojis pass through unchanged.
 */
function splitLabel(label: string): { emoji: string; rest: string } {
  const m = label.match(/^(⏳|✓|✗|⏱|⊘|·)\s*/);
  if (!m) return { emoji: '', rest: label };
  return { emoji: m[1], rest: label.slice(m[0].length) };
}

/**
 * Heartbeat events have no terminal counterpart — a main-agent phase
 * just "ends" implicitly when the next different-phase heartbeat or a
 * tool_invoked or turn_completed fires. This derives the phase duration
 * from the timestamp delta to the next ending event so the done row
 * can show "(3.2s)" like a tool/peer terminal would.
 *
 * Returns null if the event isn't a heartbeat or the phase is still
 * active (no ending event exists yet).
 */
function derivedPhaseDurationMs(
  event: PhaseEvent,
  idx: number,
  events: PhaseEvent[],
): number | null {
  if (event.kind !== 'chat_turn_heartbeat') return null;
  const phase = (event.details as any)?.phase;
  for (let j = idx + 1; j < events.length; j++) {
    const later = events[j];
    if (
      later.kind === 'chat_tool_invoked' ||
      later.kind === 'chat_turn_completed' ||
      (later.kind === 'chat_turn_heartbeat' &&
        (later.details as any)?.phase !== phase)
    ) {
      return Math.max(0, later.occurred_at_ms - event.occurred_at_ms);
    }
  }
  return null;
}

function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${s}s`;
}

/**
 * Render a label with a spinning ⏳ glyph when `spin=true`. Pulls the
 * leading status emoji out of the string so CSS can rotate just that
 * character instead of the whole line (which would drag the text
 * through the rotation too).
 */
const SpinnableLabel: React.FC<{
  text: string;
  spin: boolean;
  className?: string;
}> = ({ text, spin, className }) => {
  const { emoji, rest } = splitLabel(text);
  const doSpin = spin && emoji === '⏳';
  if (!emoji) {
    return <span className={className}>{text}</span>;
  }
  return (
    <span className={className}>
      <span
        className={`inline-block ${doSpin ? 'animate-spin' : ''}`}
        style={doSpin ? { animationDuration: '2.5s' } : undefined}
      >
        {emoji}
      </span>
      {rest && <span>{` ${rest}`}</span>}
    </span>
  );
};

// SP3 I.4 — extract the persona id from a phase-event's details for
// the Trailmaster-suffix check. chat_peer_call_* events use
// peer_persona_id; chat_squad_member_* events use persona_id. Either
// shape is acceptable.
function _personaIdFromDetails(details: any): string | null {
  if (!details || typeof details !== 'object') return null;
  return details.persona_id || details.peer_persona_id || null;
}


export const PhaseTrailBlock: React.FC<PhaseTrailBlockProps> = ({
  isLoading: loading,
  loadingStatus: status,
  events,
  expanded,
  setExpanded,
  darkMode: dm,
}) => {
  const { trailmasterPersonaId } = usePersonaSessionRoles('');
  // Ticker so live counters update every second. Only runs in live mode —
  // persisted trails render once without re-ticking.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!loading) return;
    const i = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(i);
  }, [loading]);

  // Total turn-elapsed = wall time since the first recorded phase event.
  // Falls back to 0 until the first event lands so the counter doesn't
  // flash an uninitialized number.
  const totalElapsedMs =
    loading && events.length > 0
      ? Math.max(0, nowMs - events[0].occurred_at_ms)
      : 0;

  return (
    <div className="flex justify-start mb-4">
      <div
        className={`rounded-lg overflow-hidden ${dm ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}
        style={{ minWidth: '280px' }}
      >
        {loading && (
          <div className="px-4 py-2.5 flex items-center gap-2.5">
            <div className="flex space-x-1 shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" />
              <div
                className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce"
                style={{ animationDelay: '0.15s' }}
              />
              <div
                className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce"
                style={{ animationDelay: '0.3s' }}
              />
            </div>
            {status && (
              <SpinnableLabel
                text={status}
                spin
                className={`text-xs flex-1 ${dm ? 'text-gray-400' : 'text-gray-500'}`}
              />
            )}
            {events.length > 0 && (
              <span
                className={`text-[10px] font-mono shrink-0 tabular-nums ${dm ? 'text-violet-400' : 'text-violet-500'}`}
                title="Total turn elapsed"
              >
                {formatElapsed(totalElapsedMs)}
              </span>
            )}
          </div>
        )}
        {events.length > 0 && (
          <div
            className={`${loading ? 'border-t' : ''} ${dm ? 'border-gray-700' : 'border-gray-200'}`}
          >
            <button
              onClick={() => setExpanded(!expanded)}
              className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] ${dm ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}
            >
              {expanded ? (
                <FiChevronDown className="w-2.5 h-2.5" />
              ) : (
                <FiChevronRight className="w-2.5 h-2.5" />
              )}
              <span className="flex-1 text-left">
                {loading
                  ? `Processing (${events.length} phases)`
                  : `Completed — ${events.length} phases`}
              </span>
              {loading && (
                <span
                  className="tabular-nums opacity-70"
                  title="Total turn elapsed"
                >
                  {formatElapsed(totalElapsedMs)}
                </span>
              )}
            </button>
            {expanded && (
              <div className="px-3 pb-2 space-y-0.5">
                {events.map((e, i) => {
                  const active = loading && isEventStillActive(e, i, events);
                  const activeElapsedMs = active
                    ? Math.max(0, nowMs - e.occurred_at_ms)
                    : 0;
                  // Heartbeat phases have no terminal event — derive the
                  // final duration from the next phase-ending event so
                  // "Main agent thinking" shows a final "(3.2s)" on the
                  // done row just like peer/tool lines do.
                  const derivedMs = !active
                    ? derivedPhaseDurationMs(e, i, events)
                    : null;
                  const { emoji, rest } = splitLabel(e.label);
                  const showSpin = active && emoji === '⏳';
                  // For done heartbeat phases, swap the ⏳ glyph for ✓ so
                  // the trail is scan-uniform with other terminal rows.
                  const displayEmoji =
                    !active && derivedMs !== null && emoji === '⏳'
                      ? '✓'
                      : emoji;
                  const trailingDurationMs = active
                    ? activeElapsedMs
                    : derivedMs;
                  // Sub-events stamped with `parent_tool_call_id` indent
                  // under the parent tool — works live and persisted.
                  // Tree-style left border + padding makes the hierarchy
                  // self-correlating at a glance.
                  const isChild =
                    (e.details as any)?.parent_tool_call_id != null;
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-2 ${
                        isChild
                          ? `ml-3 pl-2 border-l ${dm ? 'border-gray-700' : 'border-gray-300'}`
                          : ''
                      } ${!active && loading ? 'opacity-50' : ''}`}
                    >
                      <div
                        className={`w-1 h-1 rounded-full shrink-0 ${
                          active
                            ? 'bg-violet-400 animate-pulse'
                            : 'bg-emerald-500'
                        }`}
                      />
                      <span
                        className={`text-[10px] font-mono flex-1 flex items-center gap-1 ${
                          active
                            ? dm
                              ? 'text-violet-300'
                              : 'text-violet-600'
                            : dm
                              ? 'text-gray-500'
                              : 'text-gray-400'
                        }`}
                      >
                        {displayEmoji && (
                          <span
                            className={`inline-block ${showSpin ? 'animate-spin' : ''}`}
                            style={showSpin ? { animationDuration: '2.5s' } : undefined}
                          >
                            {displayEmoji}
                          </span>
                        )}
                        <span className="flex-1">
                          {rest}
                          {trailmasterPersonaId
                            && _personaIdFromDetails(e.details) === trailmasterPersonaId
                            && <TrailmasterCrown variant="suffix" />}
                        </span>
                        {trailingDurationMs !== null && (
                          <span className="tabular-nums opacity-80 shrink-0">
                            ({formatElapsed(trailingDurationMs)})
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Collapsed-by-default wrapper for rendering a persisted phase trail
 * above an assistant message. Each MessageBubble owns its own expanded
 * state so users can expand individual historical turns without
 * affecting neighbors.
 */
export const PersistedPhaseTrail: React.FC<{
  events: PhaseEvent[];
  darkMode: boolean;
}> = ({ events, darkMode }) => {
  const [expanded, setExpanded] = useState(false);
  if (!events || events.length === 0) return null;
  return (
    <PhaseTrailBlock
      isLoading={false}
      events={events}
      expanded={expanded}
      setExpanded={setExpanded}
      darkMode={darkMode}
    />
  );
};
